import { Button } from "@chakra-ui/button";
import { Box, HStack } from "@chakra-ui/layout";
import { FormikProps, withFormik } from "formik";
import React from "react";
import { AdminApiClient } from "../api/adminApiClient";
import { CherryHubAdminApi } from "../api/adminApiModels";
import { ChakraReactSelectField } from "../components/fields/SelectField/ChakraReactSelectField";
import { FormStack } from "../components/FormStack/FormStack";
import { AdminApiEnumConfigurations } from "../enums/AdminApiEnumConfigurations";
import { useAdminApiRequest } from "../hooks/useApiRequest";

interface MembershipManagementConfigurationFormProps {
  apiClient: AdminApiClient;
  businessId: string;
  integration: CherryHubAdminApi.Integration | null;
  isDisabled?: boolean;
  onCancel: () => void;
  onSuccess: (integration: CherryHubAdminApi.Integration) => void;
  onFailure: (message?: string) => void;
}

interface MembershipManagementConfigurationFormValues {
  memberKey: CherryHubAdminApi.MemberKeyField | undefined;
  memberPhotoKey: CherryHubAdminApi.MemberKeyField | undefined;
  membershipRenewalFeeKey: CherryHubAdminApi.MemberKeyField | undefined;
}

const InnerForm = ({
  isSubmitting,
  onCancel,
}: MembershipManagementConfigurationFormProps &
  FormikProps<MembershipManagementConfigurationFormValues>) => {
  const { data: enums, isLoading: enumLoading } = useAdminApiRequest(
    (apiClient) =>
      apiClient.getEnumConfigurations(
        AdminApiEnumConfigurations.MemberKeyField
      ),
    []
  );

  return (
    <>
      <Box w="100%" maxW="800px" minH="500px" alignSelf="flex-start">
        <FormStack>
          <ChakraReactSelectField
            horizontal
            allowEmpty={true}
            label="Member Key"
            name="memberKey"
            selectedOptionColor="blue"
            isLoading={enumLoading}
            options={
              enums?.map((item) => {
                return {
                  label: item.Value!,
                  value: item.Key!,
                };
              }) || []
            }
          />

          <ChakraReactSelectField
            horizontal
            allowEmpty={true}
            label="Member Photo Key"
            name="memberPhotoKey"
            selectedOptionColor="blue"
            isLoading={enumLoading}
            options={
              enums?.map((item) => {
                return {
                  label: item.Value!,
                  value: item.Key!,
                };
              }) || []
            }
          />

          <ChakraReactSelectField
            horizontal
            allowEmpty={true}
            label="Membership Renewal Fee Key"
            name="membershipRenewalFeeKey"
            selectedOptionColor="blue"
            isLoading={enumLoading}
            options={
              enums?.map((item) => {
                return {
                  label: item.Value!,
                  value: item.Key!,
                };
              }) || []
            }
          />
          <HStack>
            <Button
              disabled={isSubmitting}
              type="submit"
              colorScheme="cherryButton"
            >
              Save
            </Button>
            <Button disabled={isSubmitting} onClick={onCancel}>
              Cancel
            </Button>
          </HStack>
        </FormStack>
      </Box>
    </>
  );
};

export const MembershipManagementConfigurationForm = withFormik<
  MembershipManagementConfigurationFormProps,
  MembershipManagementConfigurationFormValues
>({
  mapPropsToValues: ({ integration }) => {
    return {
      memberKey:
        integration?.MembershipManagementServiceConfiguration?.MemberKey,
      memberPhotoKey:
        integration?.MembershipManagementServiceConfiguration?.MemberPhotoKey,
      membershipRenewalFeeKey:
        integration?.MembershipManagementServiceConfiguration
          ?.MembershipRenewalFeeKey,
    };
  },

  handleSubmit: async (values, { props }) => {
    // Because we use the same endpoint with `Edit` detail please make sure we include anything from the integration model
    const integrationData: Partial<CherryHubAdminApi.IntegrationUpdate> = {
      ...props.integration,
      MembershipManagementServiceConfiguration: {
        MemberKey: values.memberKey,
        MemberPhotoKey: values.memberPhotoKey,
        MembershipRenewalFeeKey: values.membershipRenewalFeeKey,
      },
    };
    const result = await props.apiClient.updateBusinessIntegration(
      props.businessId,
      props.integration?.id!,
      integrationData
    );
    if (result.ok) {
      props.onSuccess(result.data);
    } else {
      props.onFailure(result.message);
    }
  },
})(InnerForm);
